import { useState, type ReactNode } from 'react';
import { SectionHead, Toast } from '../primitives';
import {
  WorkItemsView,
  useLocalWorkItemsViewState
} from '../work-items/view';
import type { ProjectWorkItem } from './types';

export interface ProjectWorkItemsSectionProps {
  /** All project work items — used only for the section count badge. */
  items: ReadonlyArray<ProjectWorkItem>;
  /** Active org — the universal view self-fetches and scopes its queries to it. */
  activeOrgId: string | null;
  /** Scopes the Board + Table to this project's work items. */
  projectId: string;
  /** Bumps when the editor cover closes → the view refetches in place. */
  reloadSignal: number;
  onOpenWorkItem: (id: string) => void;
  /** "New ▾" header control (create-or-attach menu). */
  newMenu?: ReactNode;
}

/**
 * Project-detail Work Items section. Renders the universal <WorkItemsView>
 * (Board · Table · Timeline) scoped to this project — the exact same component
 * the standalone /work-items page uses, with component-local view state so it
 * never touches the project-canvas URL. The active tab is restored from the
 * user's last-chosen view (`penrose:work-items:view`), falling back to Board
 * when nothing is stored.
 */
export function ProjectWorkItemsSection({
  items,
  activeOrgId,
  projectId,
  reloadSignal,
  onOpenWorkItem,
  newMenu
}: ProjectWorkItemsSectionProps) {
  const viewState = useLocalWorkItemsViewState();
  // Inline-edit failure toast — held here (not inside the table tab) so it
  // persists across tab switches, matching the standalone page.
  const [toast, setToast] = useState<string | null>(null);

  return (
    <section className="pn-section" data-testid="project-work-items-section">
      <SectionHead title="Work Items" tag={String(items.length)} action={newMenu} />
      <WorkItemsView
        state={viewState}
        activeOrgId={activeOrgId}
        projectId={projectId}
        reloadSignal={reloadSignal}
        onOpenItem={onOpenWorkItem}
        onToast={setToast}
        testIdPrefix="project-work-items"
        controlBarTestIdPrefix="project-work-items"
      />
      {toast ? <Toast onDismiss={() => setToast(null)}>{toast}</Toast> : null}
    </section>
  );
}
